import {
  EditableAppointmentData,
  EditableProcedureData,
} from "../types/editableTreatmentPlan";
import { TreatmentPlanAppointment } from "../services/treatmentPlanService";
import { EMPTY_TREAT_AREA_CAPABILITIES } from "./treatAreaCapabilities";

export {
  sanitizeFeeInput,
  normalizeFeeOnBlur,
  formatFeeNumberForInput,
} from "./feeInputUtils";

export function cloneEditableAppointment(
  data: EditableAppointmentData,
): EditableAppointmentData {
  return JSON.parse(JSON.stringify(data)) as EditableAppointmentData;
}

export function normalizeFeeForCompare(fee: string): number | string {
  const cleaned = fee.replace(/^\$/, "").trim();
  if (cleaned === "") return 0;
  const num = parseFloat(cleaned);
  return Number.isNaN(num) ? cleaned : num;
}

export function valuesEqual(a: string[], b: string[]): boolean {
  const uniqueA = Array.from(new Set(a.filter(Boolean))).sort();
  const uniqueB = Array.from(new Set(b.filter(Boolean))).sort();
  if (uniqueA.length !== uniqueB.length) return false;
  return uniqueA.every((value, index) => value === uniqueB[index]);
}

export function cdtCodesEqual(a: string[], b: string[]): boolean {
  return valuesEqual(a, b);
}

export function computeAppointmentDuration(
  procedures: EditableProcedureData[],
): number {
  return procedures.reduce((sum, procedure) => {
    if (procedure.resolvedCdtCodes.length === 0) {
      return sum;
    }
    return sum + (procedure.durationMinutes || 0);
  }, 0);
}

export function resolveAppointmentDuration(
  draft: Pick<
    EditableAppointmentData,
    "procedures" | "totalDurationMinutes" | "isDurationEdited"
  >,
): number {
  const hasNoCdtCodes = draft.procedures.every(
    (procedure) => procedure.resolvedCdtCodes.length === 0,
  );

  if (hasNoCdtCodes) {
    return draft.totalDurationMinutes ?? 0;
  }

  if (draft.isDurationEdited) {
    return draft.totalDurationMinutes ?? 0;
  }

  return (
    draft.totalDurationMinutes ?? computeAppointmentDuration(draft.procedures)
  );
}

export function procedureAfterCdtCodesCleared(
  procedure: EditableProcedureData,
): EditableProcedureData {
  return {
    ...procedure,
    teeth: [],
    surfaces: [],
    durationMinutes: 0,
    fee: "0",
    isFeeEdited: false,
    treatAreaCapabilities: { ...EMPTY_TREAT_AREA_CAPABILITIES },
    codeTreatAreaFlagsByCode: {},
  };
}

export function syncProcedureIsFeeEdited(
  procedure: EditableProcedureData,
  initial?: EditableProcedureData,
): EditableProcedureData {
  if (!initial) {
    return procedure;
  }
  return {
    ...procedure,
    isFeeEdited:
      normalizeFeeForCompare(procedure.fee) !==
      normalizeFeeForCompare(initial.fee),
  };
}

function customProcedureIdEqual(a?: number, b?: number): boolean {
  return (a ?? undefined) === (b ?? undefined);
}

export function syncProcedureAfterCdtChange(
  procedure: EditableProcedureData,
  initial?: EditableProcedureData,
): EditableProcedureData {
  if (
    initial &&
    cdtCodesEqual(procedure.resolvedCdtCodes, initial.resolvedCdtCodes) &&
    customProcedureIdEqual(
      procedure.customProcedureId,
      initial.customProcedureId,
    ) &&
    valuesEqual(procedure.teeth, initial.teeth) &&
    valuesEqual(procedure.surfaces, initial.surfaces)
  ) {
    return {
      ...procedure,
      cdtCodes: [...initial.cdtCodes],
      customProcedureId: initial.customProcedureId,
      resolvedCdtCodes: [...initial.resolvedCdtCodes],
      explanation: initial.explanation,
      durationMinutes: initial.durationMinutes,
      fee: initial.fee,
      isFeeEdited: initial.isFeeEdited,
    };
  }

  // Fee/duration came from CDT recalculation, not a manual fee edit.
  return {
    ...procedure,
    isFeeEdited: false,
  };
}

export function restoreAppointmentFromInitialDraft(
  appointment: TreatmentPlanAppointment,
  initialDraft: EditableAppointmentData,
): TreatmentPlanAppointment {
  const orderedProcedures = [...initialDraft.procedures].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );
  const stepsById = new Map(
    appointment.steps.map((step) => [step.id, step]),
  );

  const steps = orderedProcedures
    .map((procedure, index) => {
      if (procedure.id == null) return null;
      const existing = stepsById.get(procedure.id);
      if (!existing) return null;
      return {
        ...existing,
        sortOrder: index + 1,
      };
    })
    .filter(
      (
        step,
      ): step is TreatmentPlanAppointment["steps"][number] => step !== null,
    );

  if (steps.length !== appointment.steps.length) {
    return appointment;
  }

  return {
    ...appointment,
    steps,
  };
}

export function hasAppointmentDraftChanges(
  draft: EditableAppointmentData,
  initial: EditableAppointmentData,
  isLastAppointment: boolean,
): boolean {
  if (draft.description !== initial.description) return true;
  if (draft.conditionalPath !== initial.conditionalPath) return true;
  if (
    (draft.totalDurationMinutes ?? 0) !== (initial.totalDurationMinutes ?? 0)
  ) {
    return true;
  }
  if (!isLastAppointment && draft.timeframeValue !== initial.timeframeValue) {
    return true;
  }

  const draftProcedures = [...draft.procedures].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );
  const initialProcedures = [...initial.procedures].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );

  if (draftProcedures.length !== initialProcedures.length) return true;

  return draftProcedures.some((procedure, index) => {
    const original = initialProcedures[index];
    return (
      procedure.text !== original.text ||
      procedure.sortOrder !== original.sortOrder ||
      procedure.durationMinutes !== original.durationMinutes ||
      normalizeFeeForCompare(procedure.fee) !==
        normalizeFeeForCompare(original.fee) ||
      !customProcedureIdEqual(
        procedure.customProcedureId,
        original.customProcedureId,
      ) ||
      !cdtCodesEqual(procedure.resolvedCdtCodes, original.resolvedCdtCodes) ||
      !valuesEqual(procedure.teeth, original.teeth) ||
      !valuesEqual(procedure.surfaces, original.surfaces)
    );
  });
}
